import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CssBaseline,
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import AxiosInstance from "../../services/networkServices";
import Spinner from "../../components/Spinner";
import { makeStyles } from "@mui/styles";
import typeOutline from "../../assets/typeOutline.svg";
import VideoPlayerDialog from "../../components/VideoPlayerDialog";
import { isVideoUrl, getPostThumbnail } from "../../utils/common";
import { showToast } from "../../store/slices/common";
import AccountActionDialog, {
  getAuthorAccountState,
} from "./AccountActionDialog";

const useStyles = makeStyles((theme) => ({
  button: {
    backgroundColor: "red !important",
    color: "#fff !important",
    borderColor: "red !important",
    "&:hover": {
      backgroundColor: "red !important",
    },
    borderRadius: "6px !important",
    fontSize: "14px !important",
    height: "40px !important",
  },
}));
const MATCH_TYPE_LABELS = {
  MEDIA_POST: "Simple",
  TEXT_POST: "Text",
  PROMPT_POST: "Prompt",
  CONTEST_POST: "Contest",
  CATEGORY_POST: "Category",
  KARAOKE_POST: "Karaoke",
  DANCE_POST: "Dance",
  ART_POST: "Art",
  GROUP_DUET_POST: "Group Duet",
  RAP_BATTLE_POST: "Rap Battle",
};

/**
 * Detail page for a reported post. With `reportType="CHEATING"` the same
 * page shows a cheating ticket: the reported post is the accused opponent's
 * pagent entry and the header additionally describes the pagent itself.
 */
const PostDetailComponent = ({ reportType = "POST" }) => {
  const classes = useStyles();
  const { postId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isCheating = reportType === "CHEATING";
  const [loading, setLoading] = useState(true);
  const [postStatus, setPostStatus] = useState("Active");
  const [postInfo, setPostInfo] = useState(null);
  const [postAuthor, setPostAuthor] = useState(null);
  const [matchInfo, setMatchInfo] = useState(null);
  const [reportedUsers, setReportedUsers] = useState([]);
  const [openDisableDialog, setOpenDisableDialog] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [accountDialog, setAccountDialog] = useState({
    open: false,
    action: null,
  });
  const [accountActionBusy, setAccountActionBusy] = useState(false);

  const handleDisableDialogOpen = () => {
    setOpenDisableDialog(true);
  };

  const handleCloseDisableDialog = () => {
    setOpenDisableDialog(false);
  };

  useEffect(() => {
    const fetchReportedUsers = async () => {
      setLoading(true);
      try {
        const reportPayload = {
          page: 1,
          limit: 10,
          id: postId,
          // Without a type the backend returns POST/COMMENT/USER rows only;
          // cheating tickets must ask for their own type explicitly.
          ...(isCheating ? { type: "CHEATING" } : {}),
        };
        const response = await AxiosInstance.post(
          "/report/list-reported-users",
          reportPayload
        );
        const { list } = response.data.data;
        // Reporter accounts can be deleted after reporting — the report row
        // must still render, so never assume userDetails exists.
        const formattedUsers = list.map((user) => ({
          id: user?._id,
          reason: user?.issue,
          username: user?.userDetails?.username || "Deleted user",
          email: user?.userDetails?.email || "—",
          description: user?.issueDescription,
          date: user?.createdAt,
        }));
        setReportedUsers(formattedUsers);
        // Only the first row carries the reported post's details and author.
        const post = list?.[0]?.postDetails || null;
        setPostInfo(post);
        // Cheating tickets also carry the accused user via reportedUserId,
        // which survives the accused entry being hard-deleted.
        setPostAuthor(
          list?.[0]?.postedBy ||
            (isCheating ? list?.[0]?.reportedUserDetails : null) ||
            null
        );
        setMatchInfo(list?.[0]?.matchDetails || null);
        if (post?.isDisabled) {
          setPostStatus("Disabled");
        }
      } catch (error) {
        console.error("Failed to fetch reported users", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReportedUsers();
  }, [postId, isCheating]);

  const handleDisablePost = async () => {
    try {
      const payload = {
        action: "DISABLE",
        type: reportType,
        id: postId,
      };
      await AxiosInstance.post("/report/action", payload);
      setPostStatus("Disabled");
      setPostInfo((prev) => (prev ? { ...prev, isDisabled: true } : prev));
      handleCloseDisableDialog();
    } catch (error) {
      console.error("Failed to disable post", error);
    }
  };

  const accountState = getAuthorAccountState(postAuthor);
  const canDisableAccount = accountState === "active";
  const canBanAccount = accountState === "active" || accountState === "disabled";

  const handleAccountAction = async () => {
    const action = accountDialog.action;
    if (!action) return;
    setAccountActionBusy(true);
    try {
      await AxiosInstance.post("/report/action", {
        action,
        type: reportType,
        id: postId,
      });
      if (action === "BAN_ACCOUNT") {
        setPostAuthor((prev) =>
          prev ? { ...prev, isAccountBlocked: true, isDeleted: true } : prev
        );
        setPostInfo((prev) =>
          prev ? { ...prev, isDeleted: true, isDisabled: true } : prev
        );
        setPostStatus("Disabled");
        dispatch(
          showToast({
            color: "success",
            msg: `@${postAuthor?.username || "User"} has been banned`,
          })
        );
      } else {
        setPostAuthor((prev) =>
          prev ? { ...prev, isAccountBlocked: true } : prev
        );
        dispatch(
          showToast({
            color: "success",
            msg: `@${postAuthor?.username || "User"}'s account has been disabled`,
          })
        );
      }
      setAccountDialog({ open: false, action: null });
    } catch (error) {
      dispatch(
        showToast({
          color: "error",
          msg:
            error?.response?.data?.message ||
            "Failed to update the author's account",
        })
      );
    } finally {
      setAccountActionBusy(false);
    }
  };

  const getAccountStatusChip = () => {
    if (accountState === "banned")
      return (
        <Chip
          label="Account Banned"
          size="small"
          color="error"
          sx={{ fontWeight: 600 }}
        />
      );
    if (accountState === "deleted")
      return (
        <Chip
          label="Account Deleted"
          size="small"
          sx={{ backgroundColor: "#9e9e9e", color: "#fff", fontWeight: 600 }}
        />
      );
    if (accountState === "disabled")
      return (
        <Chip
          label="Account Disabled"
          size="small"
          color="error"
          sx={{ fontWeight: 600 }}
        />
      );
    return (
      <Chip
        label="Account Active"
        size="small"
        color="success"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const hasPlayableVideo = isVideoUrl(postInfo?.media);
  const thumbnailUrl = getPostThumbnail(postInfo) || typeOutline;

  const getPostStatusChip = () => {
    if (postInfo?.isDeleted)
      return (
        <Chip
          label="Post Deleted"
          size="small"
          sx={{ backgroundColor: "#9e9e9e", color: "#fff", fontWeight: 600 }}
        />
      );
    if (postStatus === "Disabled")
      return (
        <Chip
          label="Post Disabled"
          size="small"
          color="error"
          sx={{ fontWeight: 600 }}
        />
      );
    return (
      <Chip
        label="Post Enabled"
        size="small"
        color="success"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const handleBreadcrumbClick = (event) => {
    event.preventDefault();
    navigate("/manage-ticket", { state: { filterType: reportType } });
  };

  const formatDateString = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Which side of the pagent the accused entry was on, so the vote line
  // reads "accused – opponent" regardless of post1/post2 order.
  const accusedIsPost1 =
    matchInfo?.post1 && String(matchInfo.post1) === String(postId);
  const accusedVotes = matchInfo
    ? accusedIsPost1
      ? matchInfo.post1Votes
      : matchInfo.post2Votes
    : null;
  const reporterVotes = matchInfo
    ? accusedIsPost1
      ? matchInfo.post2Votes
      : matchInfo.post1Votes
    : null;
  const accusedWon =
    matchInfo?.winner && postAuthor?._id
      ? String(matchInfo.winner) === String(postAuthor._id)
      : null;

  return (
    <>
      <CssBaseline />
      <Box>
        {loading ? (
          <Spinner />
        ) : (
          <Box>
            <Box sx={{ marginBottom: "24px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: {
                        xs: "15px",
                        sm: "16px",
                        md: "18px",
                        lg: "20px",
                        xl: "24px",
                      },
                    }}
                    component="h1"
                  >
                    {isCheating
                      ? "Cheating Report Details"
                      : "Post Reports Details"}
                  </Typography>
                  {getPostStatusChip()}
                  {getAccountStatusChip()}
                  {isCheating && accusedWon != null && (
                    <Chip
                      label={accusedWon ? "Accused won" : "Accused lost"}
                      size="small"
                      color={accusedWon ? "warning" : "default"}
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {hasPlayableVideo && (
                    <Button
                      onClick={() => setVideoDialogOpen(true)}
                      variant="outlined"
                      startIcon={<PlayArrow />}
                      sx={{
                        borderRadius: "6px",
                        fontSize: "14px",
                        color: "#142C5C",
                        borderColor: "#142C5C",
                        "&:hover": {
                          borderColor: "#142C5C",
                          backgroundColor: "rgba(20, 44, 92, 0.06)",
                        },
                      }}
                    >
                      Watch Video
                    </Button>
                  )}
                  <Button
                    onClick={handleDisableDialogOpen}
                    variant="contained"
                    color="error"
                    disabled={postStatus === "Disabled"}
                    sx={{
                      borderRadius: "6px",
                      fontSize: "14px",
                      backgroundColor: "red",
                    }}
                  >
                    {postStatus === "Disabled"
                      ? "Post Disabled"
                      : "Disable Post"}
                  </Button>
                  <Button
                    onClick={() =>
                      setAccountDialog({
                        open: true,
                        action: "DISABLE_ACCOUNT",
                      })
                    }
                    variant="outlined"
                    color="error"
                    disabled={!canDisableAccount || accountActionBusy}
                    sx={{
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  >
                    {canDisableAccount
                      ? "Disable Account"
                      : accountState === "deleted"
                      ? "Account Deleted"
                      : "Account Disabled"}
                  </Button>
                  <Button
                    onClick={() =>
                      setAccountDialog({
                        open: true,
                        action: "BAN_ACCOUNT",
                      })
                    }
                    variant="contained"
                    disabled={!canBanAccount || accountActionBusy}
                    sx={{
                      borderRadius: "6px",
                      fontSize: "14px",
                      backgroundColor: "#c62828",
                      "&:hover": { backgroundColor: "#b71c1c" },
                      "&.Mui-disabled": {
                        backgroundColor: "#ef9a9a",
                        color: "#fff",
                      },
                    }}
                  >
                    {accountState === "banned"
                      ? "Account Banned"
                      : accountState === "deleted"
                      ? "Account Deleted"
                      : "Ban Account"}
                  </Button>
                </Box>
              </Box>
              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  underline="hover"
                  color="inherit"
                  href="#"
                  onClick={handleBreadcrumbClick}
                >
                  {isCheating ? "Cheating" : "Posts"}
                </Link>
                <Typography color="textPrimary">
                  {isCheating ? "Accused Entry" : "Post Details"}
                </Typography>
              </Breadcrumbs>
              {isCheating && (
                <Box
                  sx={{
                    marginTop: "12px",
                    padding: "10px 14px",
                    borderRadius: "6px",
                    backgroundColor: "#fff8e1",
                    border: "1px solid #ffe082",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px 18px",
                    fontSize: "14px",
                    color: "#333",
                  }}
                >
                  <Typography sx={{ fontSize: "inherit", fontWeight: 600 }}>
                    Pagent:{" "}
                    {MATCH_TYPE_LABELS[matchInfo?.matchType] ||
                      matchInfo?.matchType ||
                      "—"}
                  </Typography>
                  {matchInfo && (
                    <Typography sx={{ fontSize: "inherit" }}>
                      Votes (accused – reporter): {accusedVotes ?? 0} –{" "}
                      {reporterVotes ?? 0}
                    </Typography>
                  )}
                  {matchInfo?.forcefullyEnded && (
                    <Typography sx={{ fontSize: "inherit" }}>
                      Ended by forfeit
                    </Typography>
                  )}
                  {matchInfo?.createdAt && (
                    <Typography sx={{ fontSize: "inherit" }}>
                      Played {formatDateString(matchInfo.createdAt)}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: "12px", color: "#777" }}>
                    Match ID: {matchInfo?._id || "—"}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                padding: "15px",
                borderRadius: "6px",
                boxShadow:
                  "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
              }}
            >
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: "calc(100vh - 200px)",
                  boxShadow: "none !important",
                }}
              >
                <Table
                  sx={{
                    border: "1px solid rgba(224, 224, 224, 1)",
                    minWidth: "100%",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "14px",
                            sm: "15px",
                            md: "15px",
                            lg: "16px",
                            xl: "16px",
                          },
                          color: "#000",
                          fontWeight: "600",
                          padding: {
                            xs: "10px 5px",
                            sm: "15px 10px",
                            md: "15px 10px",
                            lg: "20px 15px",
                            xl: "20px 15px",
                          },
                          verticalAlign: {
                            xs: "top",
                            sm: "top",
                            md: "top",
                          },
                          backgroundColor: "#e0e0e0",
                        }}
                      >
                        {isCheating ? "Accused entry" : "Reported post"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "14px",
                            sm: "15px",
                            md: "15px",
                            lg: "16px",
                            xl: "16px",
                          },
                          color: "#000",
                          fontWeight: "600",
                          padding: {
                            xs: "10px 5px",
                            sm: "15px 10px",
                            md: "15px 10px",
                            lg: "20px 15px",
                            xl: "20px 15px",
                          },
                          verticalAlign: {
                            xs: "top",
                            sm: "top",
                            md: "top",
                          },
                          backgroundColor: "#e0e0e0",
                        }}
                      >
                        {isCheating ? "Accused User" : "Post Author"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "14px",
                            sm: "15px",
                            md: "15px",
                            lg: "16px",
                            xl: "16px",
                          },
                          color: "#000",
                          fontWeight: "600",
                          padding: {
                            xs: "10px 5px",
                            sm: "15px 10px",
                            md: "15px 10px",
                            lg: "20px 15px",
                            xl: "20px 15px",
                          },
                          verticalAlign: {
                            xs: "top",
                            sm: "top",
                            md: "top",
                          },
                          backgroundColor: "#e0e0e0",
                        }}
                      >
                        Reason
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "14px",
                            sm: "15px",
                            md: "15px",
                            lg: "16px",
                            xl: "16px",
                          },
                          color: "#000",
                          fontWeight: "600",
                          padding: {
                            xs: "10px 5px",
                            sm: "15px 10px",
                            md: "15px 10px",
                            lg: "20px 15px",
                            xl: "20px 15px",
                          },
                          verticalAlign: {
                            xs: "top",
                            sm: "top",
                            md: "top",
                          },
                          backgroundColor: "#e0e0e0",
                        }}
                      >
                        Reporter Username
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "14px",
                            sm: "15px",
                            md: "15px",
                            lg: "16px",
                            xl: "16px",
                          },
                          color: "#000",
                          fontWeight: "600",
                          padding: {
                            xs: "10px 5px",
                            sm: "15px 10px",
                            md: "15px 10px",
                            lg: "20px 15px",
                            xl: "20px 15px",
                          },
                          verticalAlign: {
                            xs: "top",
                            sm: "top",
                            md: "top",
                          },
                          backgroundColor: "#e0e0e0",
                        }}
                      >
                        Reporter Email
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "14px",
                            sm: "15px",
                            md: "15px",
                            lg: "16px",
                            xl: "16px",
                          },
                          color: "#000",
                          fontWeight: "600",
                          padding: {
                            xs: "10px 5px",
                            sm: "15px 10px",
                            md: "15px 10px",
                            lg: "20px 15px",
                            xl: "20px 15px",
                          },
                          verticalAlign: {
                            xs: "top",
                            sm: "top",
                            md: "top",
                          },
                          backgroundColor: "#e0e0e0",
                        }}
                      >
                        Description
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "14px",
                            sm: "15px",
                            md: "15px",
                            lg: "16px",
                            xl: "16px",
                          },
                          color: "#000",
                          fontWeight: "600",
                          padding: {
                            xs: "10px 5px",
                            sm: "15px 10px",
                            md: "15px 10px",
                            lg: "20px 15px",
                            xl: "20px 15px",
                          },
                          verticalAlign: {
                            xs: "top",
                            sm: "top",
                            md: "top",
                          },
                          backgroundColor: "#e0e0e0",
                        }}
                      >
                        Date of Report
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportedUsers.map((reportee, index) => (
                      <TableRow key={reportee?.id}>
                        {index === 0 && (
                          <TableCell
                            sx={{
                              fontSize: {
                                xs: "12px",
                                sm: "13px",
                                md: "14px",
                                lg: "15px",
                                xl: "15px",
                              },
                              color: "#333",
                              padding: {
                                xs: "10px 5px",
                                sm: "10px",
                                md: "10px",
                                lg: "10px 15px",
                                xl: "10px 15px",
                              },
                            }}
                            rowSpan={reportedUsers.length}
                          >
                            <Box
                              sx={{ position: "relative", width: "50px" }}
                            >
                              <img
                                src={thumbnailUrl}
                                alt="Post"
                                width="50"
                                height="50"
                                style={{
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                }}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = typeOutline;
                                }}
                              />
                              {hasPlayableVideo && (
                                <Tooltip title="Play video" arrow>
                                  <Box
                                    onClick={() => setVideoDialogOpen(true)}
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "50px",
                                      height: "50px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      backgroundColor: "rgba(0, 0, 0, 0.35)",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(0, 0, 0, 0.55)",
                                      },
                                    }}
                                  >
                                    <PlayArrow sx={{ color: "#fff" }} />
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        )}
                        {index === 0 && (
                          <TableCell
                            sx={{
                              fontSize: {
                                xs: "12px",
                                sm: "13px",
                                md: "14px",
                                lg: "15px",
                                xl: "15px",
                              },
                              color: "#333",
                              padding: {
                                xs: "10px 5px",
                                sm: "10px",
                                md: "10px",
                                lg: "10px 15px",
                                xl: "10px 15px",
                              },
                            }}
                            rowSpan={reportedUsers.length}
                          >
                            {/* Post author accounts can be deleted too — keep
                                the same fallback as the reporter columns. */}
                            <Typography
                              sx={{
                                fontSize: "inherit",
                                fontWeight: 600,
                                color: "#333",
                              }}
                            >
                              {postAuthor?.username || "Deleted user"}
                            </Typography>
                            {postAuthor?.email && (
                              <Typography
                                sx={{ fontSize: "13px", color: "#777" }}
                              >
                                {postAuthor.email}
                              </Typography>
                            )}
                            <Box sx={{ marginTop: "6px" }}>
                              {getAccountStatusChip()}
                            </Box>
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            fontSize: {
                              xs: "12px",
                              sm: "13px",
                              md: "14px",
                              lg: "15px",
                              xl: "15px",
                            },
                            color: "#333",
                            padding: {
                              xs: "10px 5px",
                              sm: "10px",
                              md: "10px",
                              lg: "10px 15px",
                              xl: "10px 15px",
                            },
                          }}
                        >
                          {reportee.reason}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: {
                              xs: "12px",
                              sm: "13px",
                              md: "14px",
                              lg: "15px",
                              xl: "15px",
                            },
                            color: "#333",
                            padding: {
                              xs: "10px 5px",
                              sm: "10px",
                              md: "10px",
                              lg: "10px 15px",
                              xl: "10px 15px",
                            },
                          }}
                        >
                          {reportee.username}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: {
                              xs: "12px",
                              sm: "13px",
                              md: "14px",
                              lg: "15px",
                              xl: "15px",
                            },
                            color: "#333",
                            padding: {
                              xs: "10px 5px",
                              sm: "10px",
                              md: "10px",
                              lg: "10px 15px",
                              xl: "10px 15px",
                            },
                          }}
                        >
                          {reportee.email}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: {
                              xs: "12px",
                              sm: "13px",
                              md: "14px",
                              lg: "15px",
                              xl: "15px",
                            },
                            color: "#333",
                            padding: {
                              xs: "10px 5px",
                              sm: "10px",
                              md: "10px",
                              lg: "10px 15px",
                              xl: "10px 15px",
                            },
                          }}
                        >
                          {reportee.description}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: {
                              xs: "12px",
                              sm: "13px",
                              md: "14px",
                              lg: "15px",
                              xl: "15px",
                            },
                            color: "#333",
                            padding: {
                              xs: "10px 5px",
                              sm: "10px",
                              md: "10px",
                              lg: "10px 15px",
                              xl: "10px 15px",
                            },
                          }}
                        >
                          {formatDateString(reportee.date)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
        {/* Dialog for delete confirmation */}
        <Dialog
          sx={{
            "& .MuiDialog-container .MuiPaper-elevation": {
              borderRadius: "8px",
              width: "380px",
            },
          }}
          open={openDisableDialog}
          onClose={handleCloseDisableDialog}
        >
          <DialogTitle
            sx={{
              padding: "30px 16px 0",
              textAlign: "center",
              fontWeight: 700,
              color: "red",
            }}
          >
            Confirmation
          </DialogTitle>
          <DialogContent sx={{ padding: "0 16px 20px" }}>
            <Typography sx={{ textAlign: "center", color: "#666" }}>
              {isCheating
                ? "Are you sure you want to disable the accused entry?"
                : "Are you sure you want to disable this post?"}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: "0 16px 20px" }}>
            <Button
              onClick={handleCloseDisableDialog}
              sx={{
                border: "1px solid #888",
                color: "#888",
                borderRadius: "6px",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: "#b9b9b9",
                  color: "#222",
                  borderColor: "#b9b9b9",
                },
              }}
            >
              No
            </Button>
            <Button onClick={handleDisablePost} className={classes.button}>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
        <AccountActionDialog
          open={accountDialog.open}
          action={accountDialog.action}
          username={postAuthor?.username}
          busy={accountActionBusy}
          onClose={() => setAccountDialog({ open: false, action: null })}
          onConfirm={handleAccountAction}
        />
        <VideoPlayerDialog
          open={videoDialogOpen}
          onClose={() => setVideoDialogOpen(false)}
          url={postInfo?.media}
          fallbackUrl={postInfo?.originalMedia || ""}
          title={postInfo?.caption || "Reported Post"}
          isDisabled={postStatus === "Disabled"}
        />
      </Box>
    </>
  );
};

export default PostDetailComponent;
